import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { DispositionMapsApi } from '@core/api';
import { DispositionMap } from '@shared/entities';
import { ListResponse, Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, failureResult } from './store-helpers';

type DispositionMapsState = {
  records: DispositionMap[];
  loading: boolean;
  loaded: boolean;
  commandInProgress: boolean;
  lastCommandName: string | null;
  lastCommandResult: Result<unknown> | null;
  errorMessage: string | null;
};

const initialDispositionMapsState: DispositionMapsState = {
  records: [],
  loading: false,
  loaded: false,
  commandInProgress: false,
  lastCommandName: null,
  lastCommandResult: null,
  errorMessage: null,
};

export const DispositionMapsStore = signalStore(
  { providedIn: 'root' },
  withState(initialDispositionMapsState),
  withComputed(({ records, loading, commandInProgress }) => ({
    response: computed(() => ({ records: records() }) as ListResponse<DispositionMap>),
    isBusy: computed(() => loading() || commandInProgress()),
    byId: computed(() => new Map(records().map((map) => [map.dispositionMapId, map]))),
    dispositionMapCountsByProgramId: computed(() => {
      return records().reduce((counts, dispositionMap) => {
        const programId = dispositionMap.programId;
        if (programId !== undefined) {
          counts.set(programId, (counts.get(programId) ?? 0) + 1);
        }
        return counts;
      }, new Map<number, number>());
    }),
  })),
  withMethods((store, dispositionMapsApi = inject(DispositionMapsApi)) => {
    let loadRequestId = 0;

    const loadDispositionMaps = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void dispositionMapsApi
        .loadDispositionMaps(force)
        .then((response) => {
          if (requestId === loadRequestId) {
            patchState(store, { records: response.records, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load disposition maps.') });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    return {
      load: loadDispositionMaps,
      loadDispositionMaps,
      refresh(): void {
        loadDispositionMaps(true);
      },
      create(map: DispositionMap): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void dispositionMapsApi
          .create(map)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadDispositionMaps(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create disposition map.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create disposition map.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      update(id: number, map: DispositionMap): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void dispositionMapsApi
          .update(id, map)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadDispositionMaps(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update disposition map.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update disposition map.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void dispositionMapsApi
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadDispositionMaps(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to delete disposition map.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete disposition map.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false });
          });
      },
      clearError(): void {
        patchState(store, { errorMessage: null });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
